import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import CIREElement1 from './elements/CIREElement1';
import CIREElement2 from './elements/CIREElement2';
import CIREElement3 from './elements/CIREElement3';
import CIREElement4 from './elements/CIREElement4';
import CIREElement5 from './elements/CIREElement5';
import CIREElement6 from './elements/CIREElement6';
import CIREElement7 from './elements/CIREElement7';
import CIREElement8 from './elements/CIREElement8';
import CIREElement9 from './elements/CIREElement9';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Element configuration
const ELEMENTS = [
  { id: 'element-1', title: 'Element 1: Regulatory Framework', icon: '📘', color: '#22d3ee' },
  { id: 'element-2', title: 'Element 2: Client Relationships', icon: '📗', color: '#34d399' },
  { id: 'element-3', title: 'Element 3: KYC/KYP Requirements', icon: '📙', color: '#fbbf24' },
  { id: 'element-4', title: 'Element 4: Investment Products', icon: '📕', color: '#f87171' },
  { id: 'element-5', title: 'Element 5: Equity Securities', icon: '📓', color: '#a78bfa' },
  { id: 'element-6', title: 'Element 6: Debt Securities', icon: '📔', color: '#f472b6' },
  { id: 'element-7', title: 'Element 7: Derivatives', icon: '📒', color: '#818cf8' },
  { id: 'element-8', title: 'Element 8: Managed Products', icon: '📚', color: '#2dd4bf' },
  { id: 'element-9', title: 'Element 9: Ethics & Conduct', icon: '🎓', color: '#fb923c' },
];

const CIREElement = () => {
  const { elementId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState('');

  const currentIndex = ELEMENTS.findIndex(e => e.id === elementId);
  const currentElement = ELEMENTS[currentIndex];
  const prevElement = currentIndex > 0 ? ELEMENTS[currentIndex - 1] : null;
  const nextElement = currentIndex < ELEMENTS.length - 1 ? ELEMENTS[currentIndex + 1] : null;

  useEffect(() => {
    // Check for token in URL or localStorage
    const tokenFromStorage = localStorage.getItem('cire_access_token');
    const token = accessToken || tokenFromStorage;
    
    if (token) {
      // Store token if provided in URL
      if (accessToken) {
        localStorage.setItem('cire_access_token', accessToken);
      }
      setHasAccess(true);
    } else {
      setError('Access required. Please access this element through the main book page.');
    }
    setLoading(false);
  }, [elementId, accessToken]);

  if (!currentElement) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Element not found</h2>
          <Link to="/books/cire-interactive" style={{ color: '#D4AF37' }}>
            ← Back to CIRE Interactive Book
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(212,175,55,0.3)',
            borderTopColor: '#D4AF37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p>Loading element...</p>
        </div>
      </div>
    );
  }

  if (error || !hasAccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(30,41,59,0.8)',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.1)',
          padding: '48px',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: 'rgba(248,113,113,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            {error ? <AlertCircle size={36} style={{ color: '#f87171' }} /> : <Lock size={36} style={{ color: '#f87171' }} />}
          </div>
          <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '12px' }}>
            Access Required
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '32px', lineHeight: 1.6 }}>
            {error || 'Please use the access link from your purchase email to view this content.'}
          </p>
          <Link
            to="/books/cire-interactive"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              borderRadius: '12px',
              color: '#1e293b',
              fontWeight: '700',
              textDecoration: 'none'
            }}
          >
            Get Access
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(30,41,59,0.8)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '16px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Link
            to={`/books/cire-interactive?token=${accessToken}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'rgba(255,255,255,0.7)',
              textDecoration: 'none',
              fontSize: '14px'
            }}
          >
            <ArrowLeft size={18} />
            Back to Elements
          </Link>

          {/* Element indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>
              {currentElement.icon}
            </span>
            <span style={{
              color: 'white',
              fontWeight: '600',
              display: 'none',
              '@media (min-width: 640px)': { display: 'block' }
            }}>
              {currentElement.title}
            </span>
            <span style={{
              background: `${currentElement.color}20`,
              color: currentElement.color,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {currentIndex + 1} of {ELEMENTS.length}
            </span>
          </div>

          {/* Navigation */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {prevElement ? (
              <Link
                to={`/books/cire-interactive/${prevElement.id}?token=${accessToken}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '13px'
                }}
              >
                <ChevronLeft size={16} />
                Prev
              </Link>
            ) : (
              <div style={{ width: '70px' }} />
            )}
            {nextElement ? (
              <Link
                to={`/books/cire-interactive/${nextElement.id}?token=${accessToken}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  background: currentElement.color,
                  borderRadius: '8px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Next
                <ChevronRight size={16} />
              </Link>
            ) : (
              <Link
                to={`/books/cire-interactive?token=${accessToken}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  background: '#34d399',
                  borderRadius: '8px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Complete
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Content Area - Render actual element content */}
      {elementId === 'element-1' ? (
        <CIREElement1 />
      ) : elementId === 'element-2' ? (
        <CIREElement2 />
      ) : elementId === 'element-3' ? (
        <CIREElement3 />
      ) : elementId === 'element-4' ? (
        <CIREElement4 />
      ) : elementId === 'element-5' ? (
        <CIREElement5 />
      ) : elementId === 'element-6' ? (
        <CIREElement6 />
      ) : elementId === 'element-7' ? (
        <CIREElement7 />
      ) : elementId === 'element-8' ? (
        <CIREElement8 />
      ) : elementId === 'element-9' ? (
        <CIREElement9 />
      ) : (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '40px 20px'
        }}>
          <div style={{
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '60px',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${currentElement.color}40, ${currentElement.color}10)`,
              border: `2px solid ${currentElement.color}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              marginBottom: '32px'
            }}>
              {currentElement.icon}
            </div>
            
            <h1 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '16px'
            }}>
              {currentElement.title}
            </h1>
            
            {userName && (
              <p style={{
                fontSize: '16px',
                color: currentElement.color,
                marginBottom: '16px'
              }}>
                Welcome, {userName}!
              </p>
            )}
            
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: '600px',
              lineHeight: 1.7,
              marginBottom: '32px'
            }}>
              Interactive content for this element will be displayed here.
              Games, quizzes, and short readings coming soon!
            </p>

            <div style={{
              background: 'rgba(212,175,55,0.1)',
              border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: '12px',
              padding: '20px 32px',
              color: '#D4AF37'
            }}>
              <strong>Shareable Link:</strong>
              <br />
              <code style={{ fontSize: '12px', opacity: 0.8, wordBreak: 'break-all' }}>
                {window.location.href}
              </code>
            </div>
          </div>
        </div>
      )}

      {/* Element Navigation Grid */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px 60px'
      }}>
        <h3 style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '14px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '20px'
        }}>
          All Elements
        </h3>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {ELEMENTS.map((element, index) => (
            <Link
              key={element.id}
              to={`/books/cire-interactive/${element.id}?token=${accessToken}`}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: element.id === elementId ? element.color : 'rgba(255,255,255,0.05)',
                border: `2px solid ${element.id === elementId ? element.color : 'rgba(255,255,255,0.1)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: element.id === elementId ? '#1e293b' : 'rgba(255,255,255,0.6)',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              {index + 1}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CIREElement;
